// Web-exclusive My Jobs screen - Job Management for Facilities
// Phase 4: List and manage facility's job postings

import React, { useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, TextInput, ActivityIndicator } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Search, ClipboardList, Eye } from 'lucide-react';
import WebLayout from '../../components/web/WebLayout';
import JobDescriptionModal from '../../components/web/JobDescriptionModal.web';
import { webColors, webSpacing, webBorderRadius, webShadows } from '../../constants/webStyles';
import { getWorldCountryData } from '../../constants/worldCountries';
import { getAllJobs } from '../../services/job';
import { useIsMobile } from '../../hooks/useIsMobile';

const STATUS_FILTERS = [
  { id: 'all', label: 'All Jobs' },
  { id: 'active', label: 'Active' },
  { id: 'draft', label: 'Draft' },
  { id: 'closed', label: 'Closed' },
  { id: 'filled', label: 'Filled' },
];

const MyJobsScreen = () => {
  const navigation = useNavigation();
  const queryClient = useQueryClient();
  const [activeFilter, setActiveFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedJob, setSelectedJob] = useState(null);
  const [showJobModal, setShowJobModal] = useState(false);
  const isMobile = useIsMobile();

  // Fetch facility's jobs
  const { data: jobsData, isLoading, error } = useQuery({
    queryKey: ['myJobs', { query: `status=${activeFilter !== 'all' ? activeFilter : ''}` }],
    queryFn: getAllJobs,
  });

  const jobs = jobsData?.data?.data || jobsData?.data || [];

  // Filter jobs by search query
  const filteredJobs = jobs.filter(job => {
    const title = job.title || job.profession?.name || '';
    const matchesSearch = title.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter = activeFilter === 'all' ||
      (job.status || 'active').toLowerCase() === activeFilter;
    return matchesSearch && matchesFilter;
  });

  // Get job counts by status
  const getStatusCount = (status) => {
    if (status === 'all') return jobs.length;
    return jobs.filter(job => (job.status || 'active').toLowerCase() === status).length;
  };

  return (
    <WebLayout>
      {/* Header */}
      <View style={[styles.headerRow, isMobile && styles.headerRowMobile]}>
        <View>
          <Text style={[styles.pageTitle, isMobile && styles.pageTitleMobile]}>My Jobs</Text>
          <Text style={styles.pageSubtitle}>Manage your job postings</Text>
        </View>
        <TouchableOpacity
          style={styles.btnPostJob}
          onPress={() => navigation.navigate('PostJob')}
        >
          <Text style={styles.btnPostJobText}>+ Post a Job</Text>
        </TouchableOpacity>
      </View>

      {/* Search and Filters */}
      <View style={styles.filterSection}>
        <View style={styles.searchWrapper}>
          <View style={styles.searchIcon}>
            <Search size={16} color={webColors.textMuted} />
          </View>
          <TextInput
            style={styles.searchInput}
            placeholder="Search jobs..."
            placeholderTextColor={webColors.textMuted}
            value={searchQuery}
            onChangeText={setSearchQuery}
          />
        </View>

        <View style={[styles.statusFilters, isMobile && styles.statusFiltersMobile]}>
          {STATUS_FILTERS.map((filter) => (
            <TouchableOpacity
              key={filter.id}
              style={[
                styles.filterBtn,
                activeFilter === filter.id && styles.filterBtnActive,
              ]}
              onPress={() => setActiveFilter(filter.id)}
            >
              <Text style={[
                styles.filterBtnText,
                activeFilter === filter.id && styles.filterBtnTextActive,
              ]}>
                {filter.label}
              </Text>
              <View style={[
                styles.filterCount,
                activeFilter === filter.id && styles.filterCountActive,
              ]}>
                <Text style={[
                  styles.filterCountText,
                  activeFilter === filter.id && styles.filterCountTextActive,
                ]}>
                  {getStatusCount(filter.id)}
                </Text>
              </View>
            </TouchableOpacity>
          ))}
        </View>
      </View>

      {/* Jobs List */}
      <View style={styles.jobsContainer}>
        {isLoading ? (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color={webColors.primary} />
            <Text style={styles.loadingText}>Loading your jobs...</Text>
          </View>
        ) : filteredJobs.length === 0 ? (
          <View style={styles.emptyState}>
            <View style={styles.emptyIcon}>
              <ClipboardList size={48} color={webColors.textMuted} />
            </View>
            <Text style={styles.emptyTitle}>No jobs found</Text>
            <Text style={styles.emptyText}>
              {searchQuery
                ? 'Try a different search term'
                : activeFilter !== 'all'
                  ? `You don't have any ${activeFilter} jobs`
                  : 'Start by posting your first job'}
            </Text>
            {!searchQuery && activeFilter === 'all' && (
              <TouchableOpacity
                style={styles.emptyBtn}
                onPress={() => navigation.navigate('PostJob')}
              >
                <Text style={styles.emptyBtnText}>Post a Job</Text>
              </TouchableOpacity>
            )}
          </View>
        ) : (
          <View style={styles.jobsList}>
            {/* Table Header */}
            {!isMobile && (
              <View style={styles.tableHeader}>
                <Text style={[styles.tableHeaderCell, { flex: 2 }]}>Job Title</Text>
                <Text style={[styles.tableHeaderCell, { flex: 1 }]}>Posted</Text>
                <Text style={[styles.tableHeaderCell, { width: 140 }]}>Actions</Text>
              </View>
            )}

            {/* Table Rows */}
            {filteredJobs.map((job) => (
              <View key={job.id || job.uuid} style={[styles.tableRow, isMobile && styles.tableRowMobile]}>
                <TouchableOpacity
                  style={[styles.tableCell, { flex: 2 }, isMobile && styles.tableCellMobile]}
                  onPress={() => { setSelectedJob(job); setShowJobModal(true); }}
                >
                  <Text style={styles.jobTitle}>{job.title || job.profession?.name || 'Untitled'}</Text>
                  <Text style={styles.jobLocation}>{job.clinic?.city ? `${job.clinic.city}${job.clinic?.state ? `, ${job.clinic.state}` : ''}` : 'Location not set'}</Text>
                  {job.country && (
                    <Text style={styles.openTo}>
                      {`Open to: ${getWorldCountryData(job.country)?.flag || ''} ${getWorldCountryData(job.country)?.name || job.country}`.trim()}
                    </Text>
                  )}
                  {job.international && (
                    <Text style={styles.openTo}>
                      {`Location: ${getWorldCountryData(job.international)?.flag || ''} ${getWorldCountryData(job.international)?.name || job.international}`.trim()}
                    </Text>
                  )}
                </TouchableOpacity>
                <View style={[styles.tableCell, { flex: 1 }, isMobile && styles.tableCellMobile]}>
                  <Text style={styles.postedDate}>{formatDate(job.created_at)}</Text>
                </View>
                <View style={[styles.tableCell, { width: 140, flexDirection: 'row', alignItems: 'center', gap: 8 }, isMobile && styles.tableCellMobile]}>
                  <TouchableOpacity
                    style={styles.viewBtn}
                    onPress={() => { setSelectedJob(job); setShowJobModal(true); }}
                  >
                    <Eye size={14} color={webColors.primary} />
                    <Text style={styles.viewBtnText}>View</Text>
                  </TouchableOpacity>
                </View>
              </View>
            ))}
          </View>
        )}
      </View>
      {/* Job Description Modal */}
      <JobDescriptionModal
        visible={showJobModal}
        job={selectedJob}
        onClose={() => { setShowJobModal(false); setSelectedJob(null); }}
        onEdit={(job) => {
          setShowJobModal(false);
          setSelectedJob(null);
          navigation.navigate('PostJob', { editJobData: job });
        }}
        onRefresh={() => queryClient.invalidateQueries(['myJobs'])}
      />
    </WebLayout>
  );
};

// Helper functions
const formatDate = (date) => {
  if (!date) return '-';
  const d = new Date(date);
  return d.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
};

const styles = StyleSheet.create({
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 32,
  },
  pageTitle: {
    fontSize: 28,
    fontWeight: '700',
    color: webColors.textPrimary,
    marginBottom: 4,
  },
  pageSubtitle: {
    fontSize: 16,
    color: webColors.textMuted,
  },
  btnPostJob: {
    paddingHorizontal: 24,
    paddingVertical: 14,
    backgroundColor: webColors.primary,
    borderRadius: webBorderRadius.medium,
    ...webShadows.button,
  },
  btnPostJobText: {
    color: '#ffffff',
    fontSize: 15,
    fontWeight: '600',
  },
  filterSection: {
    marginBottom: 24,
  },
  searchWrapper: {
    position: 'relative',
    marginBottom: 16,
  },
  searchIcon: {
    position: 'absolute',
    left: 16,
    top: '50%',
    transform: [{ translateY: -8 }],
    zIndex: 1,
  },
  searchInput: {
    backgroundColor: webColors.cardBackground,
    borderWidth: 1,
    borderColor: webColors.border,
    borderRadius: webBorderRadius.medium,
    paddingHorizontal: 48,
    paddingVertical: 14,
    fontSize: 15,
    color: webColors.textPrimary,
    maxWidth: 400,
  },
  statusFilters: {
    flexDirection: 'row',
    gap: 12,
  },
  filterBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 10,
    paddingHorizontal: 16,
    backgroundColor: webColors.cardBackground,
    borderWidth: 1,
    borderColor: webColors.border,
    borderRadius: webBorderRadius.medium,
  },
  filterBtnActive: {
    backgroundColor: webColors.primaryLight,
    borderColor: webColors.primary,
  },
  filterBtnText: {
    fontSize: 14,
    fontWeight: '500',
    color: webColors.textSecondary,
  },
  filterBtnTextActive: {
    color: webColors.primary,
    fontWeight: '600',
  },
  filterCount: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    backgroundColor: webColors.backgroundAlt,
    borderRadius: 10,
  },
  filterCountActive: {
    backgroundColor: webColors.primary,
  },
  filterCountText: {
    fontSize: 12,
    fontWeight: '600',
    color: webColors.textMuted,
  },
  filterCountTextActive: {
    color: '#ffffff',
  },
  jobsContainer: {
    backgroundColor: webColors.cardBackground,
    borderWidth: 1,
    borderColor: webColors.border,
    borderRadius: webBorderRadius.large,
    overflow: 'hidden',
  },
  loadingContainer: {
    alignItems: 'center',
    padding: 48,
  },
  loadingText: {
    marginTop: 16,
    fontSize: 14,
    color: webColors.textSecondary,
  },
  emptyState: {
    alignItems: 'center',
    padding: 48,
  },
  emptyIcon: {
    marginBottom: 16,
  },
  emptyTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: webColors.textPrimary,
    marginBottom: 8,
  },
  emptyText: {
    fontSize: 14,
    color: webColors.textMuted,
    textAlign: 'center',
    marginBottom: 24,
  },
  emptyBtn: {
    paddingHorizontal: 24,
    paddingVertical: 12,
    backgroundColor: webColors.primary,
    borderRadius: webBorderRadius.medium,
  },
  emptyBtnText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#ffffff',
  },
  jobsList: {},
  tableHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 16,
    paddingHorizontal: 20,
    backgroundColor: webColors.backgroundAlt,
    borderBottomWidth: 1,
    borderBottomColor: webColors.border,
  },
  tableHeaderCell: {
    fontSize: 12,
    fontWeight: '600',
    color: webColors.textMuted,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  tableRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 16,
    paddingHorizontal: 20,
    borderBottomWidth: 1,
    borderBottomColor: webColors.borderLight,
  },
  tableCell: {
    paddingRight: 16,
  },
  jobTitle: {
    fontSize: 15,
    fontWeight: '600',
    color: webColors.textPrimary,
    marginBottom: 4,
  },
  jobLocation: {
    fontSize: 13,
    color: webColors.textMuted,
  },
  openTo: {
    fontSize: 13,
    color: webColors.textSecondary,
    marginTop: 2,
  },
  postedDate: {
    fontSize: 14,
    color: webColors.textSecondary,
  },
  viewBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 10,
    paddingVertical: 6,
    backgroundColor: webColors.primaryLight,
    borderRadius: webBorderRadius.small,
  },
  viewBtnText: {
    fontSize: 13,
    fontWeight: '600',
    color: webColors.primary,
  },
  headerRowMobile: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 16,
  },
  statusFiltersMobile: {
    flexWrap: 'wrap',
  },
  tableRowMobile: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 8,
  },
  tableCellMobile: {
    flex: 'none',
    width: '100%',
    paddingRight: 0,
  },
  pageTitleMobile: {
    fontSize: 22,
  },
});

export default MyJobsScreen;
